import React, { useEffect, useState, useCallback, useRef } from 'react';
import { kiteApi } from '../services/api';
import { PageFrame, PageHeader, Surface, Button, Note, Pill } from '@/components/ui';
import styles from './KiteConnect.module.css';
import { RefreshCcw, ExternalLink } from 'lucide-react';

interface KiteStatus {
  connected: boolean;
  access_token?: string;
}

const KiteConnect: React.FC = () => {
  const [status, setStatus] = useState<KiteStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const hasSubmitted = useRef(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await kiteApi.getStatus();
      setStatus(data);
    } catch {
      setError('Failed to fetch Kite connection status.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAutoCallback = useCallback(async (token: string) => {
    if (hasSubmitted.current) return;
    hasSubmitted.current = true;

    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await kiteApi.submitCallback(token);
      setSuccess(res.message || 'Kite Access Token generated and stored successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      await fetchStatus();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to generate access token from the received request_token.');
      hasSubmitted.current = false;
    } finally {
      setActionLoading(false);
    }
  }, [fetchStatus]);

  useEffect(() => {
    fetchStatus();

    const params = new URLSearchParams(window.location.search);
    const token = params.get('request_token');
    if (token) {
      handleAutoCallback(token);
    }
  }, [fetchStatus, handleAutoCallback]);

  const handleLoginRedirect = async () => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { login_url } = await kiteApi.getLoginUrl();
      window.location.href = login_url;
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to get Kite login URL.');
      setActionLoading(false);
    }
  };

  const maskedToken = (token: string) =>
    token.length > 12
      ? `${token.slice(0, 6)}••••••••••••${token.slice(-6)}`
      : '••••••••••••';

  return (
    <PageFrame
      header={
        <PageHeader
          title="Kite Connect"
          subtitle="Zerodha trading and data integration. The connection is re-established once every trading day."
          actions={
            <Button onClick={fetchStatus} disabled={loading} title="Refresh status">
              <RefreshCcw size={15} className={loading ? styles.spin : undefined} />
              Refresh
            </Button>
          }
        />
      }
    >
      {error && <Note tone="error">{error}</Note>}
      {success && <Note tone="success">{success}</Note>}
      {actionLoading && <Note tone="info">Redirecting to Zerodha Kite…</Note>}

      <div className={styles['top-row']}>
      <Surface
        title="System Status"
        meta={loading ? undefined : (
          status?.connected
            ? <Pill tone="positive">API connected</Pill>
            : <Pill tone="critical">Disconnected</Pill>
        )}
        padded
      >
        <div className={styles.status}>
          {loading ? (
            <span className={styles.shimmer} />
          ) : status?.connected && status.access_token ? (
            <div className={styles.token}>
              <span className={styles['token-label']}>Active token</span>
              <code>{maskedToken(status.access_token)}</code>
              <span className={styles['token-expiry']}>Valid until midnight</span>
            </div>
          ) : (
            <p className={styles['section-desc']} style={{ margin: 0 }}>
              No active token. Authorise below to connect for today.
            </p>
          )}
        </div>
      </Surface>

      <Surface title="Authorization" padded>
        <p className={styles['section-desc']}>
          To fetch live market data or place orders, establish a fresh connection with Zerodha
          once every trading day.
        </p>
        <Button
          variant="primary"
          onClick={handleLoginRedirect}
          disabled={actionLoading || loading}
          icon={<ExternalLink size={14} />}
        >
          {actionLoading ? 'Connecting…' : 'Login with Kite'}
        </Button>
      </Surface>
      </div>

      <Surface title="Workflow Guide" padded>
        <div className={styles['guide-grid']}>
          <div className={styles['guide-item']}>
            <div className={styles['guide-num']}>01</div>
            <h3>Authenticate</h3>
            <p>Click login to go to Zerodha&apos;s secure portal.</p>
          </div>
          <div className={styles['guide-item']}>
            <div className={styles['guide-num']}>02</div>
            <h3>Authorize</h3>
            <p>Log in with your Kite ID, password and 2FA.</p>
          </div>
          <div className={styles['guide-item']}>
            <div className={styles['guide-num']}>03</div>
            <h3>Return</h3>
            <p>You&apos;ll be sent back here automatically to finalise.</p>
          </div>
        </div>
      </Surface>

      <Note tone="info">
        OAT securely handles your access tokens. We never store your login credentials.
      </Note>
    </PageFrame>
  );
};

export default KiteConnect;
